export class PointVO
{
    x: number;
    y: number;

    constructor(x: number = 0, y: number = 0)
    {
        this.x = x;
        this.y = y;
    }

    clone(to?: PointVO): PointVO
    {
        to = to ?? new PointVO(0, 0);
        to.x = this.x;
        to.y = this.y;
        return to;
    }
}

export class SizeVO
{
    width:number;
    height:number;

    constructor(width:number = 0, height:number = 0){
        this.width = width;
        this.height = height;
    }

    clone(to?:SizeVO): SizeVO
    {
        to = to ?? new SizeVO(0, 0);
        to.width = this.width;
        to.height = this.height;
        return to;
    }
}

export class BoundaryVO
{
    left:number;
    right:number;
    top:number;
    bottom:number;

    constructor(left:number = 0, right:number = 0, top:number = 0, bottom:number = 0){
        this.left = left;
        this.right = right;
        this.top = top;
        this.bottom = bottom;
    }

    getWidth(prop:number = 1)
    {
        return (this.right - this.left) * prop;
    }

    getHeight(prop:number = 1)
    {
        return (this.bottom - this.top) * prop;
    }

    getCenterX()
    {
        return this.left + this.getWidth() / 2;
    }

    getCenterY()
    {
        return this.top + this.getHeight() / 2;
    }

    setWidth(value:number, centerProp:number = 0.5){
        const diff = value - this.getWidth();
        this.left -= diff * centerProp;
        this.right += diff * centerProp;
    }

    setHeight(value:number, centerProp:number = 0.5){
        const diff = value - this.getHeight();
        this.top -= diff * centerProp;
        this.bottom += diff * centerProp;
    }

    setCenterX(value:number){
        const diff = value - this.getCenterX();
        this.left += diff;
        this.right += diff;
    }

    setCenterY(value:number){
        const diff = value - this.getCenterX();
        this.top += diff;
        this.bottom += diff;
    }
}

export class BoundaryPointVO
{
    topLeft:PointVO;
    topRight:PointVO;
    bottomLeft:PointVO;
    bottomRight:PointVO;

    constructor(topLeft:PointVO, topRight:PointVO, bottomLeft:PointVO, bottomRight:PointVO){
        this.topLeft = topLeft;
        this.topRight = topRight;
        this.bottomLeft = bottomLeft;
        this.bottomRight = bottomRight;
    }

    getBoundaryVO():BoundaryVO
    {
        const left = Math.min(this.topLeft.x, this.bottomLeft.x, this.topRight.x, this.bottomRight.x);
        const right = Math.max(this.topLeft.x, this.bottomLeft.x, this.topRight.x, this.bottomRight.x);
        const top = Math.min(this.topLeft.y, this.bottomLeft.y, this.topRight.y, this.bottomRight.y);
        const bottom = Math.max(this.topLeft.y, this.bottomLeft.y, this.topRight.y, this.bottomRight.y);
        return new BoundaryVO(left, right, top, bottom);
    }

    getPointVOs():PointVO[]
    {
        return [this.topLeft, this.topRight, this.bottomLeft, this.bottomRight];
    }

    clone(): BoundaryPointVO
    {
        const topLeft = this.topLeft.clone();
        const topRight = this.topRight.clone();
        const bottomLeft = this.bottomLeft.clone();
        const bottomRight = this.bottomRight.clone();
        return new BoundaryPointVO(topLeft, topRight, bottomLeft, bottomRight);
    }
}

export interface IBoundaryProps
{
    left?:number|null
    right?:number|null
    top?:number|null
    bottom?:number|null
    centerX?:number|null
    centerY?:number|null
    width?:number|null
    height?:number|null
}

export const getBoundaryPropsWidth = (props:IBoundaryProps): number|null  =>
{
    let left:number|null = props.left ?? null;
    let right:number|null = props.right ?? null;
    let centerX:number|null = props.centerX ?? null;
    let width:number|null = props.width ?? null;
    if(left !== null){
        if(right !== null && right > left){
            return right - left;
        }
        if(centerX !== null && centerX > left){
            return (centerX - left) * 2;
        }
    }
    if(right !== null){
        if(centerX !== null && right > centerX){
            return (right - centerX) * 2;
        }
    }
    return width;
}

export const getBoundaryPropsHeight = (props:IBoundaryProps): number|null  =>
{
    let top:number|null = props.top ?? null;
    let bottom:number|null = props.bottom ?? null;
    let centerY:number|null = props.centerY ?? null;
    let height:number|null = props.height ?? null;
    if(top !== null){
        if(bottom !== null && bottom > top){
            return bottom - top;
        }
        if(centerY !== null && centerY > top){
            return (centerY - top) * 2;
        }
    }
    if(bottom !== null){
        if(centerY !== null && bottom > centerY){
            return (bottom - centerY) * 2;
        }
    }
    return height;
}